import { useTranslation } from 'react-i18next';

import { useDelayedPayoutSetup } from '../../hooks/useDelayedPayoutSetup';
import { useOrganization } from '../../hooks/useOrganizations';
import WSA from '../../WSA';

import SetupBanner from './SetupBanner';
import VerifyPayoutBanner from './VerifyPayoutBanner';

import { BillingPageEnum } from '../../common';
import {
  isStripeOnboardingStatusSetToFailed,
  isStripeOnboardingStatusSetToSucceeded,
  showSetupPayoutRibbon,
  showVerifyForPayout,
  StripeOnboardingStatus,
  updateStripeOnboardingStatus,
} from '../../helpers/stripe';
import { SEGMENT_EVENTS } from '../../segment';

interface SetupPayoutBannerProps {
  onError: (error: string) => void;
  page: string;
  // Optional, and only passed by pages that also render SetupPayoutRequirements. When Stripe has
  // told us exactly what the account is missing, that banner says so with its own fix link, and
  // this generic "connect your payout account" prompt stands down rather than contradicting it
  // (CCMS-8307). Pages without the requirements banner omit it and are unaffected.
  accountRequirements?: { currentlyDue?: string[]; pastDue?: string[] };
}

export default function SetupPayoutBanner(props: SetupPayoutBannerProps) {
  const { onError, page, accountRequirements } = props;
  const { t } = useTranslation();
  const currentOrganization = useOrganization();
  const delayedPayoutEnabled = useDelayedPayoutSetup();
  const shouldShowSetupBanner = showSetupPayoutRibbon(currentOrganization, delayedPayoutEnabled);
  const shouldShowVerifyBanner = showVerifyForPayout(currentOrganization, delayedPayoutEnabled);
  const { isReady, isAuthenticated } = WSA.Components.useWSAuth();

  if (!isReady || !isAuthenticated) return null;

  // Not verified yet: prompt them to verify with support instead of connecting.
  if (shouldShowVerifyBanner) return <VerifyPayoutBanner />;

  if (!shouldShowSetupBanner) return null;

  const handleDismissSucceededMessage = () => {
    updateStripeOnboardingStatus(currentOrganization.id, StripeOnboardingStatus.COMPLETED);
  };

  const segmentEventMap = {
    [BillingPageEnum.INVOICE]: SEGMENT_EVENTS.addPayoutAccountInvoiceConnectAccount,
    [BillingPageEnum.INVOICE_PLAN]: SEGMENT_EVENTS.addPayoutAccountInvoicePlanConnectAccount,
    [BillingPageEnum.TRANSACTION]: SEGMENT_EVENTS.addPayoutAccountTransactionConnectAccount,
    [BillingPageEnum.HOME]: SEGMENT_EVENTS.addPayoutAccountHomeConnectAccount,
  };

  const segmentEvent = segmentEventMap[page] || '';

  if (isStripeOnboardingStatusSetToFailed(currentOrganization)) {
    return (
      <SetupBanner
        type="error"
        message={t('setupPayoutBanner.failedMessage')}
        organizationId={currentOrganization.id}
        showLink
        onError={onError}
        segmentEvent={segmentEvent}
      />
    );
  }
  if (isStripeOnboardingStatusSetToSucceeded(currentOrganization)) {
    return (
      <SetupBanner
        type="success"
        message={t('setupPayoutBanner.successMessage')}
        organizationId={currentOrganization.id}
        isDismissable
        onDismiss={() => handleDismissSucceededMessage()}
      />
    );
  }
  // Stripe has named the outstanding items and SetupPayoutRequirements is already showing them
  // with a fix link. Telling the provider to "set up your Stripe account" underneath a list of
  // what is left on the account they already started reads as a contradiction, so stand down.
  const hasOutstandingRequirements =
    (accountRequirements?.currentlyDue?.length ?? 0) > 0 || (accountRequirements?.pastDue?.length ?? 0) > 0;
  if (hasOutstandingRequirements) return null;

  return (
    <SetupBanner
      type="warning"
      message={t('setupPayoutBanner.pendingMessage')}
      organizationId={currentOrganization.id}
      onError={onError}
      showLink
      segmentEvent={segmentEvent}
    />
  );
}
